<script setup lang="ts">
import { ref, reactive, computed, watch, onMounted } from 'vue';
import cssVarToHex from '@/lib/css-var-to-hex';
import slugify from '@/lib/slugify';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxHeaderControls from '@/components/Molecules/ConnxHeaderControls.vue';
import SkeletonNavigation from './SkeletonNavigation.vue';
import SkeletonGrid from './SkeletonGrid.vue';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import ConnxModal from '@/components/Molecules/ConnxModal.vue';
import {
  Grid,
} from '@progress/kendo-vue-grid';
import useColourCustomisationstore from '@/stores/Admin/colour-customisation';
import { PanelBar } from '@progress/kendo-vue-layout';
import ConnxWidgetIcon from '@/components/Molecules/ConnxWidgetIcon.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import useApplicationStore from '@/stores/application';
import useFlashMessageStore from '@/stores/flash-messages';
import ConnxInformational from '@/components/Atoms/ConnxInformational.vue';
import { ColorPalette, ColorPaletteChangeEvent } from '@progress/kendo-vue-inputs';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import ConnxColourThemeSection from './ConnxColourThemeSection.vue';
import ConnxColourPicker from '@/components/Molecules/Field/ConnxColourPicker.vue';
import { BACKGROUND_CONTRAST_RATING, LOW_CONTRAST_RATING, DEFAULT_CONTRAST_RATING } from '@/constants';

const colourCustomisationstore = useColourCustomisationstore();
const flashMessageStore = useFlashMessageStore();
const applicationStore = useApplicationStore();
applicationStore.setLoading(true);
await colourCustomisationstore.loadDefaultCssVariables();
const emits = defineEmits(['loaded']);

const previewType = ref<'desktop' | 'mobile'>('desktop');
const navigationBackgroundColorPicker = ref();
const primaryColorPicker = ref();
const mainHeadersColorPicker = ref();
const secondaryHeadersColorPicker = ref();
const gridsAndHeaders = ref();
const accordion = ref();
const confirmationModalOpen = ref(false);
const resetConfirmationOpen = ref(false);
const previewZone = ref();
const radioOption = ref(1);
const showBadContrastModal = ref(false);

const toggleBadContrastModal = () => showBadContrastModal.value = !showBadContrastModal.value;

const mainMenuGoodContrast = computed(() => {
  const navBgHex = cssVarToHex(colourCustomisationstore.navigationBackground);
  const navTextHex = cssVarToHex(colourCustomisationstore.navigationText);
  const navLogoHex = cssVarToHex(colourCustomisationstore.navigationLogoFill);
  const navAndTextRating = colourCustomisationstore.hexContrastRating(navBgHex, navTextHex);
  const navAndLogoRating = colourCustomisationstore.hexContrastRating(navBgHex, navLogoHex);

  return (
    navAndTextRating > LOW_CONTRAST_RATING &&
    navAndLogoRating > LOW_CONTRAST_RATING
  );
});

const otherComponentGoodContrast = computed(() => {
  const backgroundContrast = colourCustomisationstore.hexContrastRating(colourCustomisationstore.primary, '#FFFFFF');
  const buttonColourRating = colourCustomisationstore.hexContrastRating(colourCustomisationstore.primary, colourCustomisationstore.buttonText);

  return (
    buttonColourRating > DEFAULT_CONTRAST_RATING &&
    backgroundContrast > BACKGROUND_CONTRAST_RATING
  );
});

const headingsGoodContrast = computed(() => {
  const mainHeaderColourRating = colourCustomisationstore.hexContrastRating(colourCustomisationstore.mainHeaderText, '#FFFFFF');

  const secondaryHeaderColourRating = colourCustomisationstore.hexContrastRating(colourCustomisationstore.secondaryHeaderText, '#FFFFFF');

  return (
    mainHeaderColourRating > DEFAULT_CONTRAST_RATING &&
    secondaryHeaderColourRating > DEFAULT_CONTRAST_RATING
  );
});

const gridsAndTablesContrast = computed(() => {
  const textRating = colourCustomisationstore.hexContrastRating(colourCustomisationstore.headersText, colourCustomisationstore.headers);
  return textRating > DEFAULT_CONTRAST_RATING;
});

const accordionsContrast = computed(() => {
  const textRating = colourCustomisationstore.hexContrastRating(colourCustomisationstore.accordionText, colourCustomisationstore.accordion);
  return textRating > DEFAULT_CONTRAST_RATING;
});

const mainMenuSection = reactive({
  title: 'Main Menu Colour',
  labels: [
    'Select the menu background colour',
    'Select the menu text colour',
    'Select the menu logo colour',
  ],
  goodContrast: mainMenuGoodContrast,
});
const otherComponentSection = reactive({
  title: 'Buttons, Icons and Hyperlinks',
  labels: [
    'Select a colour for Buttons, Icons and Hyperlinks',
    'Select a colour for button text',
  ],
  goodContrast: otherComponentGoodContrast,
});
const pageHeadingSection = reactive({
  title: 'Pages and Modal Headings',
  labels: [
    'Select a colour for Page Headings',
    'Select a colour for Modal Headings',
  ],
  goodContrast: headingsGoodContrast,
});
const gridsAndTablesSection = reactive({
  title: 'Grids and Tables',
  labels: [
    'Select a background colour for grids and tables',
    'Select a colour for the heading text',
  ],
  goodContrast: gridsAndTablesContrast,
});
const accordionsSection = reactive({
  title: 'Accordions',
  labels: [
    'Select a background colour for Accordions',
    'Select a colour for the heading text',
  ],
  goodContrast: accordionsContrast,
});

const toggleConfirmationModalOpen = () => {
  confirmationModalOpen.value = !confirmationModalOpen.value;
};
const toggleResetConfirmationOpen = () => {
  resetConfirmationOpen.value = !resetConfirmationOpen.value;
};

const submitChanges = async () => {
  applicationStore.setLoading(true);
  await colourCustomisationstore.submitChanges();
  previewStyles();
  applicationStore.setLoading(false);
  flashMessageStore.pushNotification({
    id: 'Update',
    title: 'Success',
    message: 'The CSS Variables have been updated.',
    type: 'success',
  });
  toggleConfirmationModalOpen();
};
const previewStyles = () => {
  for (const [key, value] of colourCustomisationstore.cssVariableMap.entries()) {
    previewZone.value.style.setProperty(key as string, value as string);
  }

}
const resetToDefault = async () => {
  applicationStore.setLoading(true);
  await colourCustomisationstore.resetToDefault();
  previewStyles();
  applicationStore.setLoading(false);
  flashMessageStore.pushNotification({
    id: 'Reset',
    title: 'Success',
    message: 'The CSS Variables have been reset to default.',
    type: 'success',
  });
  toggleResetConfirmationOpen();
};

onMounted(() => {
  previewStyles();
  applicationStore.setLoading(false);
})

watch(() => colourCustomisationstore.navigationBackground, async (newValue, oldValue) => {
  if (applicationStore.loading) return;

  const range = await colourCustomisationstore.generateColorRange(newValue);
  if (range.has('shade-25')) {
    const contrast = colourCustomisationstore.hexContrastRating(newValue, range.get('shade-25')!.color);
    if (contrast > 1.4) {
      colourCustomisationstore.navigationBackgroundHover = range.get('shade-25')!.color;
    } else {
      colourCustomisationstore.navigationBackgroundHover = range.get('tint-25')!.color;
    }
  }
});

watch(() => colourCustomisationstore.headers, async (newValue) => {
  // If the colour is white, set it to the white theme
  // If the value is under 2 (light) set it to the light theme
  // Default theme
  if (applicationStore.loading) return;

  if (newValue === '#f5f5f5') {
    colourCustomisationstore.gridTheme = 'default';
  } else {

    const contrast = colourCustomisationstore.hexContrastRating(newValue, '#FFFFFF');
    switch (true) {
      case (contrast === 1):
        colourCustomisationstore.gridTheme = '#ffffff';
        break;
      case (contrast <= 2):
        colourCustomisationstore.gridTheme = '#cccccc';
        break;

      default:
        colourCustomisationstore.gridTheme = '#000000';
    }
  }
});

watch(() => colourCustomisationstore.accordion, async (newValue) => {
  // If the colour is white, set it to the white theme
  // If the value is under 2 (light) set it to the light theme
  // Default theme
  if (applicationStore.loading) return;

  if (newValue === '#f0f0f0') {
    colourCustomisationstore.accordionTheme = 'default';
  } else {
    const contrast = colourCustomisationstore.hexContrastRating(newValue, '#FFFFFF');
    switch (true) {
      case (contrast <= 2):
        colourCustomisationstore.accordionTheme = '#cccccc';
        break;

      default:
        colourCustomisationstore.accordionTheme = '#000000';
    }
  }

});

watch(
  () => [
    colourCustomisationstore.primary,
    colourCustomisationstore.navigationBackground,
    colourCustomisationstore.navigationLogoFill,
    colourCustomisationstore.navigationText,
    colourCustomisationstore.navigationBackgroundHover,
    colourCustomisationstore.headers,
    colourCustomisationstore.headersText,
    colourCustomisationstore.accordion,
    colourCustomisationstore.accordionText,
    colourCustomisationstore.buttonText,
    colourCustomisationstore.mainHeaderText,
    colourCustomisationstore.secondaryHeaderText,
  ],
  () => previewStyles(),
);

</script>

<template>
  <div ref="previewZone">
    <connx-modal
      scrollable
      title="Low Contrast Detected"
      :open="showBadContrastModal"
      @close="toggleBadContrastModal()"
    >
      <p>The combination of the selected background colour and font colour may not meet accessibility standards, making
        it difficult for some users to read the text. For optimal readability and accessibility, ensure sufficient
        contrast between the text and background.</p>
      <p>Contrast is important to ensure content is readable for all users, especially those with visual impairments.
      </p>
      <a
        href="https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html"
        target="_blank"
      >Learn more about
        contrast requirements from WCAG.</a>
    </connx-modal>
    <connx-confirmation-modal
      :open="confirmationModalOpen"
      heading-text="Submit the Customisations?"
      confirmation-button-text="Yes"
      @confirmed="submitChanges()"
      @close="toggleConfirmationModalOpen()"
    >
    </connx-confirmation-modal>
    <connx-confirmation-modal
      :open="resetConfirmationOpen"
      confirmation-button-text="Yes, reset"
      @confirmed="resetToDefault()"
      @close="toggleResetConfirmationOpen()"
    >
      <p>WARNING: ALL of your current customisations will be cleared.</p>
    </connx-confirmation-modal>
    <div class="tw-flex tw-justify-between tw-flex-wrap tw-w-full">
      <connx-informational class="lg:tw-max-w-3xl tw-mb-4">
        <p>With the colour configuration, you can tailor the major colours of ConnX to reflect your
          organisations brand identity.</p>
        <p class="tw-mb-0">Please choose a colour that allows for easy reading and accessibility, therefore refrain from
          using
          lighter colours like white, as these will not be easy to read.</p>
      </connx-informational>
      <connx-header-controls>
        <div class="tw-w-auto">
          <connx-button
            class="tw-w-full"
            @click="toggleResetConfirmationOpen()"
          >
            Reset to default
          </connx-button>
        </div>
        <div class="tw-w-auto">
          <connx-button
            icon="fas fa-check"
            icon-position="before"
            button-style="success"
            class="tw-w-full"
            @click="toggleConfirmationModalOpen()"
          >
            Save
          </connx-button>
        </div>
      </connx-header-controls>
    </div>
    <div class="tw-mt-10 tw-flex tw-justify-end tw-items-center tw-gap-x-4">
      <p>Preview:</p>
      <connx-field
        :type="InputType.ButtonGroup"
        v-model="previewType"
        name="preview-type"
        :options="[{ value: 'desktop', label: 'Desktop' }, { value: 'mobile', label: 'Mobile' }]"
      ></connx-field>
    </div>

    <connx-colour-theme-section
      @show-bad-contrast-modal="toggleBadContrastModal()"
      :title="mainMenuSection.title"
      :labels="mainMenuSection.labels"
      :good-contrast="mainMenuSection.goodContrast"
    >
      <template #input-1>
        <connx-colour-picker
          :id="slugify(mainMenuSection.labels[0])"
          v-model="colourCustomisationstore.navigationBackground"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="navigationBackgroundColorPicker"
          v-model="colourCustomisationstore.navigationBackground"
        />
      </template>
      <template #input-2>
        <color-palette
          class="LogoColorPicker"
          :id="slugify(mainMenuSection.labels[1])"
          :palette="['#171718', '#FFFFFF', '#56575A']"
          :value="colourCustomisationstore.navigationText"
          :tile-size="40"
          @change="($event: ColorPaletteChangeEvent) => colourCustomisationstore.navigationText = $event.value"
        />
        <connx-colour-picker
          id="nav-text"
          v-model="colourCustomisationstore.navigationText"
          label-position="right"
          :selected="!['#171718', '#FFFFFF', '#56575A'].includes(colourCustomisationstore.navigationText)"
        ></connx-colour-picker>
      </template>
      <template #input-3>
        <color-palette
          class="LogoColorPicker"
          :id="slugify(mainMenuSection.labels[2])"
          :palette="['#171718', '#FFFFFF', '#515154']"
          :value="colourCustomisationstore.navigationLogoFill"
          :tile-size="40"
          @change="($event: ColorPaletteChangeEvent) => colourCustomisationstore.navigationLogoFill = $event.value"
        />
      </template>

      <template #preview>
        <skeleton-navigation :type="previewType" />
      </template>
    </connx-colour-theme-section>

    <connx-colour-theme-section
      @show-bad-contrast-modal="toggleBadContrastModal()"
      :title="otherComponentSection.title"
      :labels="otherComponentSection.labels"
      :good-contrast="otherComponentSection.goodContrast"
    >
      <template #input-1>
        <connx-colour-picker
          :id="slugify(otherComponentSection.labels[0])"
          v-model="colourCustomisationstore.primary"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="primaryColorPicker"
          v-model="colourCustomisationstore.primary"
        />
      </template>
      <template #input-2>
        <color-palette
          class="LogoColorPicker"
          :id="slugify(otherComponentSection.labels[1])"
          :palette="['#171718', '#FFFFFF', '#515154']"
          :value="colourCustomisationstore.buttonText"
          :tile-size="40"
          @change="($event: ColorPaletteChangeEvent) => colourCustomisationstore.buttonText = $event.value"
        />
      </template>
      <template #preview>
        <div v-if="previewType === 'desktop'">
          <div class="tw-px-8 tw-min-w-fit tw-flex tw-gap-x-10 tw-items-center">
            <div
              value="Add"
              class="btn btn-primary m-btn m-btn--icon"
            >
              <span><connx-icon
                  name="fas fa-plus"
                  class="tw-w-4 !tw-h-4"
                ></connx-icon><span>Add</span></span>
            </div>

            <div class="tw-flex">
              <connx-widget-icon
                icon="far fa-calendar-alt"
                text="Calendar"
                link="#"
              ></connx-widget-icon>
              <connx-widget-icon
                icon="far fa-suitcase"
                text="Leave"
                link="#"
              ></connx-widget-icon>
              <connx-widget-icon
                icon="far fa-file-invoice-dollar"
                text="Pay"
                link="#"
              ></connx-widget-icon>
              <connx-widget-icon
                icon="far fa-clock"
                text="Timecard"
                link="#"
              ></connx-widget-icon>
            </div>
            <a
              href="#"
              class="tw-whitespace-nowrap"
            >Hyperlink example</a>
          </div>
        </div>
        <div v-if="previewType === 'mobile'">
          <div class="tw-px-8 tw-flex tw-flex-wrap tw-items-center tw-gap-10">
            <connx-icon
              name="fas fa-plus"
              class="tw-text-connx-primary tw-w-auto tw-h-6"
            ></connx-icon>
            <connx-icon
              name="fas fa-chevron-right"
              class="tw-text-connx-primary tw-w-auto tw-h-6"
            ></connx-icon>
            <div class="tw-flex-grow tw-w-full md:tw-w-auto">
              <ul>
                <li
                  @click="() => radioOption = 1"
                  :class="{
                    'tw-border tw-border-solid tw-border-connx-grey-tint-70 tw-bg-connx-white': radioOption !== 1,
                    'tw-z-10 tw-relative tw-text-connx-button-text tw tw-bg-connx-primary': radioOption === 1,
                  }"
                  class="tw-cursor-pointer tw-overflow-hidden tw-rounded -tw-mb-0.5 tw-px-4 tw-py-2"
                >
                  Radio Option 1
                </li>
                <li
                  @click="() => radioOption = 2"
                  :class="{
                    'tw-border tw-border-solid tw-border-connx-grey-tint-70 tw-bg-connx-white': radioOption !== 2,
                    'tw-z-10 tw-relative tw-text-connx-button-text tw tw-bg-connx-primary': radioOption === 2,
                  }"
                  class="tw-cursor-pointer tw-overflow-hidden tw-rounded -tw-mb-0.5 tw-px-4 tw-py-2"
                >
                  Radio Option 2
                </li>
                <li
                  @click="() => radioOption = 3"
                  :class="{
                    'tw-border tw-border-solid tw-border-connx-grey-tint-70 tw-bg-connx-white': radioOption !== 3,
                    'tw-z-10 tw-relative tw-text-connx-button-text tw tw-bg-connx-primary': radioOption === 3,
                  }"
                  class="tw-cursor-pointer tw-overflow-hidden tw-rounded -tw-mb-0.5 tw-px-4 tw-py-2"
                >
                  Radio Option 3
                </li>
              </ul>
            </div>
            <connx-button
              button-style="brand-primary"
              icon="fas fa-arrow-right"
              class="tw-flex-grow tw-w-full md:tw-w-auto"
            >Next</connx-button>
          </div>
        </div>

      </template>
    </connx-colour-theme-section>

    <connx-colour-theme-section
      @show-bad-contrast-modal="toggleBadContrastModal()"
      :title="pageHeadingSection.title"
      :labels="pageHeadingSection.labels"
      :good-contrast="pageHeadingSection.goodContrast"
    >
      <template #input-1>
        <connx-colour-picker
          :id="slugify(pageHeadingSection.labels[0])"
          v-model="colourCustomisationstore.mainHeaderText"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="mainHeadersColorPicker"
          v-model="colourCustomisationstore.mainHeaderText"
        />
      </template>
      <template #input-2>
        <connx-colour-picker
          :id="slugify(pageHeadingSection.labels[1])"
          v-model="colourCustomisationstore.secondaryHeaderText"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="secondaryHeadersColorPicker"
          v-model="colourCustomisationstore.secondaryHeaderText"
        />
      </template>
      <template #preview>
        <div v-if="previewType === 'desktop'">
          <div class="tw-px-8 tw-flex tw-flex-wrap tw-gap-10 tw-items-start">
            <article class="tw-bg-connx-white tw-max-w-sm tw-w-full">
              <header class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-4">
                <h3 class="tw-mx-4 tw-text-connx-main-header tw-mb-0">
                  Emergency Contacts</h3>
              </header>
              <section class="tw-py-2 tw-px-4">
              </section>
            </article>

            <div class="tw-flex tw-flex-col">
              <div class="
                  tw-bg-connx-white
                  tw-rounded-md
                  tw-mx-auto
                  tw-border
                  tw-border-solid
                  tw-flex-1
                  tw-w-full
                  tw-max-w-md
                  tw-shadow-sm
                  tw-flex
                ">
                <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
                  <header
                    class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-8 tw-flex tw-items-center tw-justify-between"
                  >
                    <h1 class="tw-text-2xl tw-font-semibold tw-text-connx-secondary-header tw-mb-0">
                      Add New Emergency Contact
                    </h1>
                    <button
                      type="button"
                      class="tw-ml-4 tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
                    >
                      <connx-icon
                        name="fal fa-times"
                        class="tw-w-3"
                      />
                    </button>
                  </header>
                  <main class="
                      tw-mx-auto
                      tw-w-full
                      tw-max-w-5xl
                      tw-h-full
                      tw-flex
                      tw-flex-col
                      tw-justify-start
                    ">
                    <p></p>
                  </main>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-else
          class="tw-px-8"
        >
          <div
            class="tw-bg-connx-white tw-max-w-md tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-py-2 tw-px-8 tw-flex tw-items-center tw-justify-between"
          >
            <h5 class="tw-text-connx-main-header">Personal Details</h5>
          </div>
        </div>
      </template>
    </connx-colour-theme-section>

    <connx-colour-theme-section
      @show-bad-contrast-modal="toggleBadContrastModal()"
      :title="gridsAndTablesSection.title"
      :labels="gridsAndTablesSection.labels"
      :good-contrast="gridsAndTablesSection.goodContrast"
    >
      <template #input-1>
        <connx-colour-picker
          :id="slugify(gridsAndTablesSection.labels[0])"
          v-model="colourCustomisationstore.headers"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="gridsAndHeaders"
          v-model="colourCustomisationstore.headers"
        />
      </template>
      <template #input-2>
        <color-palette
          class="LogoColorPicker"
          :id="slugify(gridsAndTablesSection.labels[1])"
          :palette="['#171718', '#FFFFFF', '#515154']"
          :value="colourCustomisationstore.headersText"
          :tile-size="40"
          @change="($event: ColorPaletteChangeEvent) => colourCustomisationstore.headersText = $event.value"
        />
      </template>
      <template #preview>
        <div v-if="previewType === 'desktop'">
          <div class="tw-flex tw-justify-center">
            <skeleton-grid />
          </div>
        </div>
        <div v-if="previewType === 'mobile'">
          <div class="tw-px-8">
            <grid
              ref="grid"
              class="tw-text-sm tw-mx-auto tw-max-w-md"
              scrollable="none"
              :columns="[
                { field: 'Department' },
                { field: 'PrimaryDept', title: 'Primary Dept' },
                { field: 'ReportsTo', title: 'Reports To' },
                { field: 'Assigned' }
              ]"
              :data-items="[
                { Department: 'Human Resources', PrimaryDept: 'Y', ReportsTo: 'Reed RICHARDS', Assigned: '1/05/2019' }
              ]"
              :row-class="() => 'tw-group'"
            >
            </grid>
          </div>
        </div>
      </template>
    </connx-colour-theme-section>

    <connx-colour-theme-section
      @show-bad-contrast-modal="toggleBadContrastModal()"
      :title="accordionsSection.title"
      :labels="accordionsSection.labels"
      :good-contrast="accordionsSection.goodContrast"
    >
      <template #input-1>
        <connx-colour-picker
          :id="slugify(accordionsSection.labels[0])"
          v-model="colourCustomisationstore.accordion"
          label-position="right"
        >
        </connx-colour-picker>
        <connx-field
          class="tw-hidden lg:tw-block tw-max-w-28"
          :type="InputType.Text"
          name="navigation-value"
          ref="accordion"
          v-model="colourCustomisationstore.accordion"
        />
      </template>
      <template #input-2>
        <color-palette
          class="LogoColorPicker"
          :id="slugify(accordionsSection.labels[1])"
          :palette="['#171718', '#FFFFFF', '#515154']"
          :value="colourCustomisationstore.accordionText"
          :tile-size="40"
          @change="($event: ColorPaletteChangeEvent) => colourCustomisationstore.accordionText = $event.value"
        />
      </template>
      <template #preview>
        <div
          :class="`Accordion--${previewType}`"
          class="tw-flex tw-justify-center"
        >
          <PanelBar
            class="tw-w-full tw-max-w-xl"
            :expand-mode="'single'"
            :items="[
              {
                expanded: true,
                title: 'Leave',
                content: 'first',
              },
              {
                title: 'My Teammates',
                content: 'second',
              },
              {
                title: 'Workflow Forms',
                content: 'third',
              },
            ]"
          >
            <template v-slot:first>
              <div>
                <a
                  href="#"
                  class="tw-block"
                >Current Leave Applications</a>
                <a
                  href="#"
                  class="tw-block"
                >Leave Balances</a>
                <a
                  href="#"
                  class="tw-block"
                >Leave Dashboard</a>
                <a
                  href="#"
                  class="tw-block"
                >Leave History</a>
                <a
                  href="#"
                  class="tw-block"
                >New Leave Application</a>
                <a
                  href="#"
                  class="tw-block"
                >Previous Leave Applications</a>
              </div>
            </template>
            <template v-slot:second>
              <div>
                <ul>
                  <li><a href="#">Request Employee Changes</a></li>
                  <li><a href="#">Request to Separate</a></li>
                  <li><a href="#">Notice of Resignation or Retirement</a></li>
                </ul>
              </div>
            </template>
          </PanelBar>
        </div>
      </template>
    </connx-colour-theme-section>

    <h5 class="tw-mb-6">Non-Configurable Components</h5>
    <div class="tw-flex tw-justify-between tw-flex-wrap tw-w-full">
      <connx-informational
        class="lg:tw-max-w-3xl tw-mb-4"
        type="info"
      >
        <p class="tw-text-connx-white">Please note that the following elements are not included in the colour scheme
          configuration and will retain their default appearance:</p>

        <ul class="tw-list-disc tw-ml-4">
          <li class="tw-text-connx-white">System fonts</li>
          <li class="tw-text-connx-white">System notifications</li>
          <li class="tw-text-connx-white">Any report layouts</li>
          <li class="tw-text-connx-white">Third-party Integrated Widgets</li>
          <li class="tw-text-connx-white">Online help pages</li>
          <li class="tw-text-connx-white">Buttons (except the 'Add' button): Other buttons such as 'Edit' 'Delete'
            'Preview'
            etc. </li>
        </ul>
      </connx-informational>
    </div>
  </div>
</template>